import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useThirdPartyApi,
  WaveDirectionMeasurement,
  WaveHeightMeasurement,
} from "~/api/GeneratedApi";
import { TabPanel } from "~/components/GenericTab";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { WaveMapItem } from "../types/mapItems";
import { GenericPopup } from "./GenericPopup";

const useStyles = makeStyles(() => ({
  tab: {
    margin: 0,
  },
}));

type Props = {
  map: Map;
} & (ClickProps | HoverProps);

type ClickProps = {
  clickData: WaveMapItem;
  hoverData?: WaveMapItem;
};

type HoverProps = {
  hoverData: WaveMapItem;
  clickData?: WaveMapItem;
};

type HistoryMeasurement = {
  waveHeight: WaveHeightMeasurement;
  waveDirection?: WaveDirectionMeasurement;
};

export const WavePopup = ({
  hoverData,
  clickData,
  map,
}: Props): JSX.Element | null => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [historyMeasurements, setHistoryMeasurements] =
    useState<HistoryMeasurement[]>();
  const { t } = useTranslation("weather");
  const dateFormatter = useDateFormatter();
  const { getWaveHistory } = useThirdPartyApi();

  const data = clickData ?? hoverData;

  const { data: historyData, isLoading: historyLoading } = useCustomQuery(
    { key: "wave-history", id: clickData?.id },
    () => getWaveHistory({ station: clickData?.id }),
    {
      enabled: !!clickData?.id,
    },
  );

  // Map history response to state
  useEffect(() => {
    if (!historyData || !clickData?.id) return;
    const stationHistory = historyData?.find((h) => h.id === clickData.id);
    if (!stationHistory || !stationHistory.waveHeightMeasurements) return;
    const history: HistoryMeasurement[] =
      stationHistory.waveHeightMeasurements.map((h) => {
        // Matching direction
        const dir = stationHistory.waveDirectionMeasurements?.find(
          (d) => d.timestamp.valueOf() === h.timestamp.valueOf(),
        );
        return {
          waveHeight: h,
          waveDirection: dir,
        };
      });
    setHistoryMeasurements(history);
  }, [historyData, clickData?.id]);

  if (!data) return null;

  const {
    stationName,
    latestHeightValue,
    latestDirectionValue,
    region,
    waveHeightMeasurements,
    waveDirectionMeasurements,
  } = data;

  const handleChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number,
  ) => {
    setTab(newValue);
  };

  return (
    <GenericPopup
      map={map}
      avatarText={
        <>
          {latestHeightValue
            ? `${latestHeightValue.value} ${latestHeightValue.unit}`
            : "-"}
          {latestDirectionValue && (
            <ArrowDownwardIcon
              style={{
                transform: `rotate(${latestDirectionValue.value}deg)`,
                fontSize: "1.25em",
                marginRight: 2,
              }}
            />
          )}
        </>
      }
      title={`${t("wave-height")} - ${stationName ?? ""}`}
      subheader={region ?? ""}
      clicked={!!clickData}
      content={
        <>
          <Tabs
            value={tab}
            onChange={handleChange}
            TabIndicatorProps={{ className: classes.tab }}
            variant="fullWidth"
            indicatorColor="secondary"
          >
            <Tab label={t("latest-measurements")} className={classes.tab} />
            <Tab
              label={t("measurement-history")}
              disabled={historyLoading}
              className={classes.tab}
            />
          </Tabs>
          <TabPanel index={0} value={tab}>
            {waveHeightMeasurements?.length ? (
              <>
                <Typography align="center" variant="body2" noWrap>
                  {`${t("wave-height")} | ${t("wave-direction")}`}
                </Typography>
                <Divider />
                <List>
                  {waveHeightMeasurements?.map((m, index) => {
                    // Matching direction
                    const dir = waveDirectionMeasurements?.find(
                      (d) => d.timestamp.valueOf() === m.timestamp.valueOf(),
                    );
                    return (
                      <ListItem
                        dense
                        alignItems="center"
                        style={{ textAlign: "center" }}
                        key={m.timestamp.valueOf()}
                        divider={index < waveHeightMeasurements.length - 1}
                      >
                        <ListItemText
                          primary={
                            <>
                              {`${m.value} ${m.unit}`}
                              {dir && (
                                <>
                                  {" | "}
                                  <ArrowDownwardIcon
                                    style={{
                                      transform: `rotate(${dir.value}deg)`,
                                      fontSize: "inherit",
                                    }}
                                  />
                                </>
                              )}
                            </>
                          }
                          secondary={dateFormatter({ date: m.timestamp })}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </>
            ) : (
              <Typography align="center" variant="body2">
                {t("no-measurements")}
              </Typography>
            )}
          </TabPanel>
          <TabPanel index={1} value={tab}>
            {historyMeasurements?.length ? (
              <>
                <Typography align="center" variant="body2" noWrap>
                  {`${t("wave-height")} | ${t("wave-direction")}`}
                </Typography>
                <Divider />
                <List>
                  {historyMeasurements?.map((m, index) => {
                    return (
                      <ListItem
                        dense
                        alignItems="center"
                        style={{ textAlign: "center" }}
                        key={m.waveHeight.timestamp.valueOf()}
                        divider={index < historyMeasurements.length - 1}
                      >
                        <ListItemText
                          primary={
                            <>
                              {`${m.waveHeight.value} ${m.waveHeight.unit}`}
                              {m.waveDirection && (
                                <>
                                  {" | "}
                                  <ArrowDownwardIcon
                                    style={{
                                      transform: `rotate(${m.waveDirection.value}deg)`,
                                      fontSize: "inherit",
                                    }}
                                  />
                                </>
                              )}
                            </>
                          }
                          secondary={dateFormatter({
                            date: m.waveHeight.timestamp,
                          })}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </>
            ) : (
              <Typography align="center" variant="body2">
                {t("no-measurements")}
              </Typography>
            )}
          </TabPanel>
        </>
      }
    />
  );
};
